// FitCheckr Service - Handles image uploads and retrieval for fit checking
import { ref, getDownloadURL } from "firebase/storage";
import { FitCheckApi } from "./fit-check.api";
import { storage } from "../../firebase/config";
import { uploadFile, getFileURL, deleteFile } from "../../firebase/storage";
import { logger } from "../../services";
import { RecentFits } from "../../types";
import { loadRecentFits, loadFitImages } from "../recentfits/recent-fits.service";

export interface UserFitImage {
  url: string;
  uploadedAt: Date;
  fileName: string;
}

export interface RecentFitsWithImages {
  fits: RecentFits[];
  fitImages: Record<string, string>;
  latestFitImage: string | null;
}

/**
 * Get the storage path for a user's fit image
 */
const getUserFitImagePath = (userId: string): string => {
  return `users/${userId}/fit-image.jpg`;
};

/**
 * Upload a fit image for a user
 * This will overwrite any existing fit image for the user
 */
export const uploadUserFitImage = async (
  userId: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<UserFitImage> => {
  try {

    // Delete existing image first (if any) to ensure we only have one
    try {
      await deleteUserFitImage(userId);
    } catch (error) {
      // Ignore errors if no existing image
    }

    // Upload the new image
    const storagePath = getUserFitImagePath(userId);
    const url = await uploadFile(file, storagePath, onProgress);

    const result: UserFitImage = {
      url,
      uploadedAt: new Date(),
      fileName: file.name,
    };

    return result;
  } catch (error) {
    await logger.error("image_upload_service_failed", error as Error, {
      user_id: userId.substring(0, 8),
      file_size: file.size,
    });
    throw new Error("Failed to upload fit image. Please try again.");
  }
};

/**
 * Get a user's fit image if it exists
 */
export const getUserFitImage = async (userId: string): Promise<UserFitImage | null> => {
  try {
    // Loading user image - no need for analytics logging

    const storagePath = getUserFitImagePath(userId);
    const url = await getFileURL(storagePath);

    const result: UserFitImage = {
      url,
      uploadedAt: new Date(), // We don't have exact upload time from storage metadata
      fileName: "fit-image.jpg",
    };

    // Image loaded successfully
    return result;
  } catch (error) {
    // If file doesn't exist, return null (not an error)
    if (error instanceof Error && error.message.includes("object-not-found")) {
      // No image found - this is normal
      return null;
    }

    await logger.error("fit_image_load_failed", error as Error, { user_id: userId.substring(0, 8) });
    throw new Error("Failed to load fit image. Please try again.");
  }
};

/**
 * Delete a user's fit image
 */
export const deleteUserFitImage = async (userId: string): Promise<void> => {
  try {
    const storagePath = getUserFitImagePath(userId);
    await deleteFile(storagePath);
  } catch (error) {
    await logger.error("image_delete_failed", error as Error, { user_id: userId.substring(0, 8) });
    throw new Error("Failed to delete fit image. Please try again.");
  }
};

/**
 * Check if a user has a fit image
 */
export const userHasFitImage = async (userId: string): Promise<boolean> => {
  try {
    const image = await getUserFitImage(userId);
    return image !== null;
  } catch (error) {
    await logger.error("image_check_failed", error as Error, { user_id: userId.substring(0, 8) });
    return false;
  }
};

/**
 * Load recent fits for a user with their images
 */
export const loadRecentFitsWithImages = async (
  userId: string,
  maxResults: number = 5,
): Promise<RecentFitsWithImages> => {
  try {
    // Loading recent fits - no need for analytics logging

    // Load recent fits using the existing service
    const fits = await loadRecentFits(userId, "all", maxResults);

    if (fits.length === 0) {
      return {
        fits: [],
        fitImages: {},
        latestFitImage: null,
      };
    }

    const fitsForImages = fits.slice(0, maxResults);
    const fitImages = await loadFitImages(fitsForImages);

    // Load the latest fit image separately for main display
    let latestFitImage: string | null = null;
    if (fits[0]?.generatedImagePath) {
      try {
        const imageRef = ref(storage, fits[0].generatedImagePath);
        latestFitImage = await getDownloadURL(imageRef);
        // Also store in fitImages for sidebar
        fitImages[fits[0].id] = latestFitImage;
      } catch (error) {
        // Ignore individual image loading errors
      }
    }

    // Successfully loaded recent fits and images

    return {
      fits,
      fitImages,
      latestFitImage,
    };
  } catch (error) {
    await logger.error("recent_fits_load_service_failed", error as Error, { user_id: userId.substring(0, 8) });
    throw new Error("Failed to load recent fits. Please try again.");
  }
};

/**
 * Call the backend API to check fit with AI
 */
export const checkFitWithAI = async (
  request: { shopLink: string; originalClothingImageUrl: string },
  authToken: string,
): Promise<{ success: boolean; message?: string; error?: string; generatedImagePath?: string; description?: string }> => {
  try {
    await logger.userAction("fit_check_api_call_started");

    // Create a singleton instance of the FitCheck API
    const fitCheckApi = new FitCheckApi();

    // Set the auth token for this request
    fitCheckApi.setAuthToken(authToken);

    const result = await fitCheckApi.generateFitImage(request);

    if (result.success) {
      await logger.userAction("fit_check_completed", { shop_link: request.shopLink });
    } else {
      await logger.error("fit_check_api_failed", new Error(result.message || result.error), { shop_link: request.shopLink });
    }

    return result;
  } catch (error) {
    await logger.error("fit_check_api_error", error as Error);

    // Check if this is an axios error with response data
    if (error && typeof error === "object" && "response" in error) {
      const axiosError = error as any;
      if (axiosError.response?.data) {
        const errorData = axiosError.response.data;
        // Return the structured error response from the API
        return {
          success: false,
          error: errorData.error || errorData.message,
          message: errorData.message,
        };
      }
    }

    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to generate fit visualization. Please try again.",
    );
  }
};
